// Обратная связь из шапки: кнопка на каждом экране и окно с двумя вкладками —
// «Написать» и «Мои замечания».
//
// **Зачем на каждом экране.** Система ещё строится, и сбой замечают там, где
// он случился: посреди задачи, на разборе, в лаборатории. Кнопка, до которой
// надо дойти, означает замечание, которое не написано, — или написано потом,
// по памяти, без экрана и без скриншота.
//
// **Что ставится само.** Экран, задача или тема, устройство — из приложения;
// кто — по ключу; когда — сервер. Человек пишет только, что не так, и
// прикладывает скриншот: вставкой, перетаскиванием или выбором файла. Место
// показано в окне до отправки, чтобы было видно, что именно уйдёт.
//
// **Почему окно, а не экран.** Экран увёл бы с задачи, и черновик ответа
// пропал бы вместе с ней (роутера нет, вид — состояние App). Окно модальное:
// под ним ничего не меняется, и место, записанное при открытии, остаётся
// верным до отправки.
//
// **Тон.** Ни «спасибо!», ни смайлов: «Отправлено как №12» — номер и есть
// подтверждение, по нему нить находится в истории.

import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import {
  day,
  describeDevice,
  MAX_SHOTS,
  onFeedbackRequest,
  shrinkImage,
  STATUS_LABEL,
  uploadShots,
  when,
  type FeedbackAs,
  type FeedbackPlace,
  type NoteStatus,
} from "../lib/feedback";
import { reasonOf } from "../lib/errors";
import { IconCross, IconFeedback } from "./Icons";
import "../feedback.css";

// ─── Скриншоты ──────────────────────────────────────────────────────────────

type Shot = { blob: Blob; url: string };

/** Снимки черновика: сжимаются при добавлении, живут до отправки. */
export function useShots() {
  const [shots, setShots] = useState<Shot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const live = useRef<Shot[]>([]);
  live.current = shots;
  useEffect(() => () => live.current.forEach((s) => URL.revokeObjectURL(s.url)), []);

  const add = (files: Blob[]) => {
    const images = files.filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) {
      if (files.length > 0) setError("это не картинка");
      return;
    }
    setError(null);
    void (async () => {
      const room = MAX_SHOTS - live.current.length;
      if (room <= 0) {
        setError(`не больше ${MAX_SHOTS} скриншотов`);
        return;
      }
      if (images.length > room) setError(`не больше ${MAX_SHOTS} скриншотов — лишние не взяты`);
      const next: Shot[] = [];
      for (const f of images.slice(0, room)) {
        try {
          const blob = await shrinkImage(f);
          next.push({ blob, url: URL.createObjectURL(blob) });
        } catch (e: unknown) {
          setError(reasonOf(e));
        }
      }
      setShots((prev) => [...prev, ...next].slice(0, MAX_SHOTS));
    })();
  };

  const remove = (i: number) =>
    setShots((prev) => {
      const gone = prev[i];
      if (gone !== undefined) URL.revokeObjectURL(gone.url);
      return prev.filter((_, n) => n !== i);
    });

  const clear = () =>
    setShots((prev) => {
      prev.forEach((s) => URL.revokeObjectURL(s.url));
      return [];
    });

  /** Ctrl+V / ⌘V с картинкой в буфере. Текст вставляется как обычно. */
  const onPaste = (e: ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (files.some((f) => f.type.startsWith("image/"))) {
      e.preventDefault();
      add(files);
    }
  };

  return { shots, error, add, remove, clear, onPaste };
}
type ShotsState = ReturnType<typeof useShots>;

/** Поле скриншотов: миниатюры, выбор файла, приём перетаскивания. */
export function Shots({ state }: { state: ShotsState }) {
  const [over, setOver] = useState(false);
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    state.add(Array.from(e.dataTransfer.files));
  };
  return (
    <div
      className={`fb-shots${over ? " fb-shots--over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
    >
      {state.shots.length > 0 ? (
        <div className="fb-shots__row">
          {state.shots.map((s, i) => (
            <div className="fb-shot" key={s.url}>
              <img src={s.url} alt={`Скриншот ${i + 1}`} />
              <button
                type="button"
                className="fb-shot__x"
                title="Убрать"
                aria-label={`Убрать скриншот ${i + 1}`}
                onClick={() => state.remove(i)}
              >
                <IconCross size={12} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <div className="fb-shots__hint">
        <label className="btn btn--quiet fb-shots__pick">
          Приложить скриншот
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => {
              state.add(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
          />
        </label>
        <span className="faint">или вставьте из буфера, или перетащите сюда</span>
      </div>
      {state.error !== null ? <div className="fb-error">{state.error}</div> : null}
    </div>
  );
}

// ─── Нить ───────────────────────────────────────────────────────────────────

export type FeedbackTurn = { by: "tutor" | "learner" | "dev"; at: number; text: string; files: string[] };

/** Реплики нити. Скриншоты — картинками, по клику открываются целиком. */
export function Turns({
  turns,
  who,
}: {
  turns: FeedbackTurn[];
  who: (by: FeedbackTurn["by"]) => string;
}) {
  return (
    <div className="fb-turns">
      {turns.map((t, i) => (
        <div className={`fb-turn${t.by === "dev" ? " fb-turn--dev" : ""}`} key={i}>
          <div className="card__label">
            {who(t.by)} · {when(t.at)}
          </div>
          {t.text !== "" ? <p className="fb-turn__text">{t.text}</p> : null}
          {t.files.length > 0 ? (
            <div className="fb-turn__files">
              {t.files.map((url, n) => (
                <a href={url} target="_blank" rel="noreferrer" key={n}>
                  <img src={url} alt={`Скриншот ${n + 1}`} />
                </a>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function StatusChip({ status }: { status: NoteStatus }) {
  return <span className={`fb-status fb-status--${status}`}>{STATUS_LABEL[status]}</span>;
}

/** Строка о скриншотах: когда удалятся или когда удалены. */
export function PurgeLine({ purgeAt, purgedAt }: { purgeAt: number | null; purgedAt: number | null }) {
  if (purgeAt !== null) return <div className="faint">Скриншоты удалятся {day(purgeAt)}</div>;
  if (purgedAt !== null) return <div className="faint">Скриншоты удалены {day(purgedAt)}</div>;
  return null;
}

// ─── Кнопка и окно ──────────────────────────────────────────────────────────

type Tab = "new" | "mine";

export function Feedback({ as, place }: { as: FeedbackAs; place: FeedbackPlace }) {
  const unseen = useQuery(api.feedback.unseen, { as });
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("new");
  // Место и устройство — на момент нажатия, а не отправки: окно модальное,
  // но размер окна браузера человек может поменять, пока пишет.
  const [snap, setSnap] = useState<{ place: FeedbackPlace; device: string; hint?: string } | null>(
    null,
  );
  // Черновик живёт дольше окна: закрыл нечаянно — текст и скриншоты на месте.
  const [text, setText] = useState("");
  const shots = useShots();
  const [sent, setSent] = useState<number | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = dialog.current;
    if (open && d !== null && !d.open) d.showModal();
  }, [open]);

  const unseenCount = unseen ?? 0;
  const show = () => {
    setSnap({ place, device: describeDevice() });
    setSent(null);
    setTab(unseenCount > 0 ? "mine" : "new");
    setOpen(true);
  };
  const close = () => dialog.current?.close();

  // Окно, открытое с места (src/lib/feedback.ts, `requestFeedback`): своё
  // место и своя подсказка, сразу на вкладке «Написать».
  useEffect(
    () =>
      onFeedbackRequest(({ place: at, hint }) => {
        setSnap({ place: at, device: describeDevice(), hint });
        setSent(null);
        setTab("new");
        setOpen(true);
      }),
    [],
  );

  const label =
    unseenCount > 0 ? `Обратная связь — есть ответ разработчика` : "Сообщить об ошибке или предложить";

  return (
    <>
      <button
        type="button"
        className="appnav__btn fb-btn"
        title={label}
        aria-label={label}
        aria-haspopup="dialog"
        onClick={show}
      >
        <IconFeedback size={19} />
        {unseenCount > 0 ? <span className="fb-btn__dot" aria-hidden="true" /> : null}
      </button>
      {open && snap !== null ? (
        <dialog
          ref={dialog}
          className="fb-dialog"
          aria-labelledby="fb-title"
          onClose={() => setOpen(false)}
          // Клик по подложке закрывает окно: подложка — сам <dialog> вне рамки.
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div className="fb-frame">
            <div className="fb-head">
              <h2 id="fb-title" className="fb-title">
                Обратная связь
              </h2>
              <button type="button" className="fb-close" onClick={close} aria-label="Закрыть">
                <IconCross size={16} />
              </button>
            </div>
            <div className="fb-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === "new"}
                className="fb-tab"
                onClick={() => setTab("new")}
              >
                Написать
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "mine"}
                className="fb-tab"
                onClick={() => setTab("mine")}
              >
                Мои замечания
                {unseenCount > 0 ? <span className="fb-tab__count num">{unseenCount}</span> : null}
              </button>
            </div>
            <div className="fb-body">
              {tab === "new" ? (
                <Compose
                  as={as}
                  place={snap.place}
                  device={snap.device}
                  hint={snap.hint}
                  text={text}
                  setText={setText}
                  shots={shots}
                  onSent={(no) => {
                    setSent(no);
                    setText("");
                    shots.clear();
                    setTab("mine");
                  }}
                />
              ) : (
                <Mine as={as} sent={sent} />
              )}
            </div>
          </div>
        </dialog>
      ) : null}
    </>
  );
}

function Compose({
  as,
  place,
  device,
  hint,
  text,
  setText,
  shots,
  onSent,
}: {
  as: FeedbackAs;
  place: FeedbackPlace;
  device: string;
  hint?: string;
  text: string;
  setText: (s: string) => void;
  shots: ShotsState;
  onSent: (no: number) => void;
}) {
  const getUrl = useMutation(api.feedback.uploadUrl);
  const send = useMutation(api.feedback.send);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const empty = text.trim() === "" && shots.shots.length === 0;

  const submit = () => {
    if (busy || empty) return;
    setBusy(true);
    setFailed(null);
    void (async () => {
      try {
        const files = await uploadShots(
          shots.shots.map((s) => s.blob),
          () => getUrl({ as }),
        );
        const { no } = await send({
          as,
          screen: place.screen,
          where: place.where,
          itemKey: place.itemKey,
          skillId: place.skillId,
          device,
          text,
          files,
        });
        onSent(no);
      } catch (e: unknown) {
        setFailed(reasonOf(e));
      } finally {
        setBusy(false);
      }
    })();
  };

  // Экран, задача и устройство уходят с замечанием молча: ученику их
  // перечень ничего не говорит, а место над полем занимает.
  return (
    <div className="fb-compose" onPaste={shots.onPaste}>
      <textarea
        className="field field--text fb-text"
        rows={5}
        value={text}
        autoFocus
        placeholder={
          hint ?? "Что не так или что сделать иначе. Если сбой — что вы делали и что ожидали увидеть."
        }
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
        }}
      />
      <Shots state={shots} />
      {failed !== null ? <div className="notice">{failed}</div> : null}
      <div className="fb-actions">
        <button type="button" className="btn btn--primary" onClick={submit} disabled={busy || empty}>
          {busy ? "Отправляю…" : "Отправить"}
        </button>
      </div>
    </div>
  );
}

type MineRow = {
  no: number;
  status: NoteStatus;
  kind: "app" | "theory" | "solution";
  where: string | null;
  skillId: string | null;
  itemKey: string | null;
  createdAt: number;
  purgeAt: number | null;
  purgedAt: number | null;
  unseen: boolean;
  turns: FeedbackTurn[];
};

function Mine({ as, sent }: { as: FeedbackAs; sent: number | null }) {
  const rows = useQuery(api.feedback.mine, { as });
  const markSeen = useMutation(api.feedback.markSeen);
  // Какие нити были непрочитанными при открытии: отметка «прочитано» уходит
  // сразу, а подсветка должна дожить до конца просмотра.
  const fresh = useRef<Set<number> | null>(null);
  const hasUnseen = rows?.some((r) => r.unseen) ?? false;
  useEffect(() => {
    if (hasUnseen) void markSeen({ as }).catch(() => undefined);
    // `as` — новый объект на каждый рендер; значим только факт непрочитанного.
  }, [hasUnseen]);
  if (rows !== undefined && fresh.current === null) {
    fresh.current = new Set(rows.filter((r) => r.unseen).map((r) => r.no));
    if (sent !== null) fresh.current.add(sent);
  }

  if (rows === undefined) return <div className="skeleton" style={{ height: 80 }} />;
  if (rows.length === 0) {
    return <p className="muted">Замечаний пока нет. Ответ разработчика появится здесь.</p>;
  }
  return (
    <div className="fb-list">
      {sent !== null ? <div className="fb-sent">Отправлено как №{sent}.</div> : null}
      {rows.map((r) => (
        <MineNote key={r.no} as={as} row={r} fresh={fresh.current?.has(r.no) ?? false} />
      ))}
    </div>
  );
}

/** Подпись места в истории. Замечания взрослого к разбору и к задаче
 *  (`kind` theory/solution) пришли не из шапки, и «где» у них нет. */
function noteWhere(row: MineRow): string {
  if (row.kind === "theory") return `разбор темы ${row.skillId ?? ""}`;
  if (row.kind === "solution") return `задача ${row.itemKey ?? ""}`;
  return row.where ?? "о системе";
}

function MineNote({ as, row, fresh }: { as: FeedbackAs; row: MineRow; fresh: boolean }) {
  const [replying, setReplying] = useState(false);
  return (
    <div className={`fb-note${fresh ? " fb-note--fresh" : ""}`}>
      <div className="fb-note__head">
        <span className="fb-no">№{row.no}</span>
        <StatusChip status={row.status} />
        <span className="fb-note__where">{noteWhere(row)}</span>
        <span className="faint fb-note__date">{day(row.createdAt)}</span>
      </div>
      <Turns turns={row.turns} who={(by) => (by === "dev" ? "Разработчик" : "Вы")} />
      <div className="fb-note__foot">
        <PurgeLine purgeAt={row.purgeAt} purgedAt={row.purgedAt} />
        {replying ? null : (
          <button type="button" className="btn btn--quiet" onClick={() => setReplying(true)}>
            {row.status === "question" ? "Уточнить" : "Ответить"}
          </button>
        )}
      </div>
      {replying ? (
        <Reply as={as} no={row.no} status={row.status} onDone={() => setReplying(false)} />
      ) : null}
    </div>
  );
}

function Reply({
  as,
  no,
  status,
  onDone,
}: {
  as: FeedbackAs;
  no: number;
  status: NoteStatus;
  onDone: () => void;
}) {
  const getUrl = useMutation(api.feedback.uploadUrl);
  const reply = useMutation(api.feedback.reply);
  const [text, setText] = useState("");
  const shots = useShots();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const empty = text.trim() === "" && shots.shots.length === 0;

  const submit = () => {
    if (busy || empty) return;
    setBusy(true);
    setFailed(null);
    void (async () => {
      try {
        const files: Id<"_storage">[] = await uploadShots(
          shots.shots.map((s) => s.blob),
          () => getUrl({ as }),
        );
        await reply({ as, no, text, files });
        shots.clear();
        onDone();
      } catch (e: unknown) {
        setFailed(reasonOf(e));
      } finally {
        setBusy(false);
      }
    })();
  };

  // Что сделает ответ со статусом — сказано до отправки, а не после.
  const hint =
    status === "question"
      ? "Ответ вернёт замечание разработчику."
      : status === "done" || status === "declined"
        ? "Ответ откроет замечание снова."
        : null;
  return (
    <div className="fb-reply" onPaste={shots.onPaste}>
      {hint !== null ? <div className="faint">{hint}</div> : null}
      <textarea
        className="field field--text fb-text"
        rows={3}
        value={text}
        autoFocus
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
        }}
      />
      <Shots state={shots} />
      {failed !== null ? <div className="notice">{failed}</div> : null}
      <div className="fb-actions">
        <button type="button" className="btn btn--quiet" onClick={onDone} disabled={busy}>
          Отмена
        </button>
        <button type="button" className="btn btn--primary" onClick={submit} disabled={busy || empty}>
          {busy ? "Отправляю…" : "Отправить"}
        </button>
      </div>
    </div>
  );
}
